import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Text, Button, VStack, HStack, Flex, Badge, IconButton, Input, Select,
  FormControl, FormLabel, Spinner, useToast, Tooltip, Table, Thead, Tbody, Tr, Th, Td,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  useDisclosure, Switch, Tag,
} from '@chakra-ui/react';
import {
  FiPlus, FiEdit2, FiTrash2, FiCreditCard, FiList, FiCheck, FiX, FiAlertTriangle,
} from 'react-icons/fi';
import PageShell from './PageShell';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

const CARD_TYPES = ['Visa','Mastercard','Amex','Maestro','Discover','UnionPay','Other'];

const BLANK = {
  user_id:'', bank_account_id:'', card_type:'Visa', name_on_card:'',
  last_five:'', issue_date:'', expiry_date:'', notes:'', is_active: true,
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { month:'short', year:'numeric' }) : '—';
const isExpired = (d) => d && new Date(d) < new Date();
const isExpiringSoon = (d) => {
  if (!d) return false;
  const exp = new Date(d);
  const soon = new Date(); soon.setMonth(soon.getMonth() + 2);
  return exp > new Date() && exp <= soon;
};

export default function PaymentCardsPage({ isOpen, onClose, fullPage, onBack }) {
  const { user } = useAuth();
  const canWrite = ['admin','accounts_manager'].includes(user?.role);
  const [cards, setCards] = useState([]);
  const [users, setUsers] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loadingTx, setLoadingTx] = useState(false);
  const txDisclosure = useDisclosure();
  const toast = useToast();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cardsRes, usersRes, banksRes] = await Promise.all([
        api.get('/payment-cards'),
        api.get('/users'),
        api.get('/bank-accounts?is_own=true'),
      ]);
      setCards(cardsRes.data);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setBankAccounts(banksRes.data);
    } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(() => { if (fullPage || isOpen) fetchAll(); }, [fullPage, isOpen, fetchAll]);

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const openAdd = () => { setForm(BLANK); setEditId(null); setShowForm(true); };
  const openEdit = (c) => {
    setForm({
      user_id: c.user_id||'', bank_account_id: c.bank_account_id||'',
      card_type: c.card_type||'Visa', name_on_card: c.name_on_card||'',
      last_five: c.last_five||'', is_active: !!c.is_active,
      issue_date: c.issue_date?.split('T')[0]||'',
      expiry_date: c.expiry_date?.split('T')[0]||'',
      notes: c.notes||'',
    });
    setEditId(c.id); setShowForm(true);
  };

  const save = async () => {
    if (!form.name_on_card) { toast({ title:'Name on card required', status:'warning', duration:2000 }); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/payment-cards/${editId}`, form);
      else await api.post('/payment-cards', form);
      toast({ title:'Saved', status:'success', duration:1500 });
      setShowForm(false); setEditId(null); fetchAll();
    } catch(e) { toast({ title: e.response?.data?.error||'Failed', status:'error', duration:2000 }); }
    setSaving(false);
  };

  const toggleActive = async (card) => {
    try {
      await api.put(`/payment-cards/${card.id}`, { is_active: !card.is_active });
      fetchAll();
    } catch(e) { toast({ title:'Failed', status:'error', duration:2000 }); }
  };

  const deleteCard = async (id) => {
    try { await api.delete(`/payment-cards/${id}`); fetchAll(); } catch(e) {}
  };

  const viewTransactions = async (card) => {
    setSelectedCard(card); setLoadingTx(true); txDisclosure.onOpen();
    try {
      const { data } = await api.get(`/payment-cards/${card.id}/transactions`);
      setTransactions(data);
    } catch(e) { setTransactions([]); }
    setLoadingTx(false);
  };

  const headerRight = canWrite ? (
    <Button size="sm" leftIcon={<FiPlus size={13}/>} colorScheme="blue" variant="outline"
      onClick={openAdd}>Add Card</Button>
  ) : null;

  const cardTypeColor = (t) => ({ Visa:'blue', Mastercard:'red', Amex:'green', Maestro:'purple' })[t] || 'gray';

  return (
    <>
    <PageShell fullPage={fullPage} onBack={onBack} isOpen={isOpen} onClose={onClose}
      title="Payment Cards" headerRight={headerRight}>
      <Box maxW="1100px" mx="auto" py={4} px={4}>

        {!canWrite && (
          <Flex bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" p={3} mb={4} align="center" gap={2}>
            <FiAlertTriangle color="orange" />
            <Text fontSize="12px" color="orange.700">View only — admin or accounts manager role required to make changes.</Text>
          </Flex>
        )}

        {/* Add / Edit form */}
        {showForm && canWrite && (
          <Box bg="blue.50" p={4} borderRadius="xl" mb={5} border="1px solid" borderColor="blue.200">
            <Text fontSize="13px" fontWeight="700" color="blue.700" mb={3}>{editId ? 'Edit Card' : 'Add Payment Card'}</Text>
            <VStack spacing={3} align="stretch">
              <HStack spacing={3}>
                <FormControl isRequired flex={2}>
                  <FormLabel fontSize="11px" mb={1}>Name on Card</FormLabel>
                  <Input size="sm" bg="white" value={form.name_on_card} onChange={set('name_on_card')} placeholder="e.g. PAUL EMERTON" />
                </FormControl>
                <FormControl maxW="130px">
                  <FormLabel fontSize="11px" mb={1}>Card Type</FormLabel>
                  <Select size="sm" bg="white" value={form.card_type} onChange={set('card_type')}>
                    {CARD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </Select>
                </FormControl>
                <FormControl maxW="120px">
                  <FormLabel fontSize="11px" mb={1}>Last 5 Digits</FormLabel>
                  <Input size="sm" bg="white" value={form.last_five} onChange={set('last_five')}
                    placeholder="12345" maxLength={5} fontFamily="mono" />
                </FormControl>
              </HStack>
              <HStack spacing={3}>
                <FormControl flex={1}>
                  <FormLabel fontSize="11px" mb={1}>Assigned to User</FormLabel>
                  <Select size="sm" bg="white" value={form.user_id} onChange={set('user_id')}>
                    <option value="">— No specific user —</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.display_name||u.username}</option>)}
                  </Select>
                </FormControl>
                <FormControl flex={1}>
                  <FormLabel fontSize="11px" mb={1}>Withdraws From Account</FormLabel>
                  <Select size="sm" bg="white" value={form.bank_account_id} onChange={set('bank_account_id')}>
                    <option value="">— No linked account —</option>
                    {bankAccounts.map(b => <option key={b.id} value={b.id}>{b.account_name||b.bank_name}{b.account_number?` — ${b.account_number}`:''}</option>)}
                  </Select>
                </FormControl>
                <FormControl maxW="130px">
                  <FormLabel fontSize="11px" mb={1}>Issue Date</FormLabel>
                  <Input size="sm" bg="white" type="date" value={form.issue_date} onChange={set('issue_date')} />
                </FormControl>
                <FormControl maxW="130px">
                  <FormLabel fontSize="11px" mb={1}>Expiry Date</FormLabel>
                  <Input size="sm" bg="white" type="date" value={form.expiry_date} onChange={set('expiry_date')} />
                </FormControl>
              </HStack>
              <HStack spacing={3}>
                <FormControl flex={2}>
                  <FormLabel fontSize="11px" mb={1}>Notes</FormLabel>
                  <Input size="sm" bg="white" value={form.notes} onChange={set('notes')} placeholder="Optional notes…" />
                </FormControl>
                {editId && (
                  <FormControl maxW="140px">
                    <FormLabel fontSize="11px" mb={1}>Active</FormLabel>
                    <HStack mt={2}>
                      <Switch isChecked={form.is_active} colorScheme="green"
                        onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} />
                      <Text fontSize="12px" color={form.is_active ? 'green.600' : 'red.500'}>
                        {form.is_active ? 'Active' : 'Inactive'}
                      </Text>
                    </HStack>
                  </FormControl>
                )}
              </HStack>
              <Flex justify="flex-end" gap={2}>
                <Button size="sm" variant="ghost" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Button>
                <Button size="sm" colorScheme="blue" onClick={save} isLoading={saving}>
                  {editId ? 'Update Card' : 'Add Card'}
                </Button>
              </Flex>
            </VStack>
          </Box>
        )}

        {/* Cards list */}
        {loading ? (
          <Flex justify="center" py={12}><Spinner size="lg" color="blue.400" /></Flex>
        ) : cards.length === 0 ? (
          <Box textAlign="center" py={16} color="gray.400">
            <FiCreditCard size={40} style={{ margin:'0 auto 12px', opacity:0.3 }} />
            <Text fontSize="15px" mb={1}>No payment cards yet</Text>
            {canWrite && <Text fontSize="12px">Click "Add Card" to register a company payment card.</Text>}
          </Box>
        ) : (
          <Box border="1px solid" borderColor="gray.200" borderRadius="xl" overflow="hidden">
            <Table size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th fontSize="10px">Card</Th>
                  <Th fontSize="10px">Holder</Th>
                  <Th fontSize="10px">Assigned To</Th>
                  <Th fontSize="10px">Bank Account</Th>
                  <Th fontSize="10px">Issued</Th>
                  <Th fontSize="10px">Expires</Th>
                  <Th fontSize="10px" textAlign="center">Status</Th>
                  <Th fontSize="10px" textAlign="center">Txns</Th>
                  <Th w="100px"/>
                </Tr>
              </Thead>
              <Tbody>
                {cards.map(c => {
                  const expired = isExpired(c.expiry_date);
                  const expiringSoon = isExpiringSoon(c.expiry_date);
                  return (
                    <Tr key={c.id} bg={!c.is_active ? 'gray.50' : expired ? 'red.50' : 'white'}
                      opacity={!c.is_active ? 0.6 : 1} _hover={{ bg: 'blue.50' }}>
                      <Td py={2.5}>
                        <HStack spacing={2}>
                          <Badge colorScheme={cardTypeColor(c.card_type)} fontSize="9px" px={2}>{c.card_type}</Badge>
                          {c.last_five && (
                            <Text fontSize="11px" fontFamily="mono" color="gray.600">•••• {c.last_five}</Text>
                          )}
                        </HStack>
                      </Td>
                      <Td py={2.5}>
                        <Text fontSize="12px" fontWeight="600">{c.name_on_card}</Text>
                        {c.notes && <Text fontSize="9px" color="gray.400" noOfLines={1}>{c.notes}</Text>}
                      </Td>
                      <Td py={2.5} fontSize="11px" color="gray.600">{c.user_name || '—'}</Td>
                      <Td py={2.5} fontSize="11px" color="gray.600">{c.bank_account_name || c.bank_name || '—'}</Td>
                      <Td py={2.5} fontSize="11px" color="gray.500">{fmtDate(c.issue_date)}</Td>
                      <Td py={2.5}>
                        <HStack spacing={1}>
                          <Text fontSize="11px" color={expired ? 'red.600' : expiringSoon ? 'orange.600' : 'gray.500'}
                            fontWeight={expired || expiringSoon ? '700' : '400'}>
                            {fmtDate(c.expiry_date)}
                          </Text>
                          {expired && <Badge colorScheme="red" fontSize="8px">Expired</Badge>}
                          {expiringSoon && <Badge colorScheme="orange" fontSize="8px">Expiring soon</Badge>}
                        </HStack>
                      </Td>
                      <Td textAlign="center" py={2.5}>
                        <Badge colorScheme={c.is_active ? 'green' : 'gray'} fontSize="9px">
                          {c.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </Td>
                      <Td textAlign="center" py={2.5}>
                        <Button size="xs" variant="ghost" colorScheme="blue" leftIcon={<FiList size={11}/>}
                          onClick={() => viewTransactions(c)}>
                          {c.transaction_count > 0 ? c.transaction_count : 'View'}
                        </Button>
                      </Td>
                      <Td py={2.5}>
                        {canWrite && (
                          <HStack spacing={0}>
                            <Tooltip label={c.is_active ? 'Mark inactive' : 'Mark active'}>
                              <IconButton icon={c.is_active ? <FiX size={11}/> : <FiCheck size={11}/>}
                                size="xs" variant="ghost" colorScheme={c.is_active ? 'red' : 'green'}
                                onClick={() => toggleActive(c)} aria-label="Toggle" />
                            </Tooltip>
                            <IconButton icon={<FiEdit2 size={11}/>} size="xs" variant="ghost" colorScheme="blue"
                              onClick={() => openEdit(c)} aria-label="Edit" />
                            <IconButton icon={<FiTrash2 size={11}/>} size="xs" variant="ghost" colorScheme="red"
                              onClick={() => deleteCard(c.id)} aria-label="Delete" />
                          </HStack>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>
    </PageShell>

    {/* Transactions modal */}
    <Modal isOpen={txDisclosure.isOpen} onClose={txDisclosure.onClose} size="2xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="80vh">
        <ModalHeader fontSize="md" pb={1}>
          <HStack>
            <FiCreditCard />
            <Text>{selectedCard?.card_type} •••• {selectedCard?.last_five || '—'}</Text>
            <Badge colorScheme="blue" fontSize="10px">{selectedCard?.name_on_card}</Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loadingTx ? (
            <Flex justify="center" py={8}><Spinner /></Flex>
          ) : transactions.length === 0 ? (
            <Text fontSize="13px" color="gray.400" textAlign="center" py={8}>No transactions recorded for this card.</Text>
          ) : (
            <Table size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th fontSize="10px">Date</Th>
                  <Th fontSize="10px">Amount</Th>
                  <Th fontSize="10px">Invoice</Th>
                  <Th fontSize="10px">Description</Th>
                  <Th fontSize="10px">Reference</Th>
                </Tr>
              </Thead>
              <Tbody>
                {transactions.map(t => (
                  <Tr key={t.id} _hover={{ bg:'gray.50' }}>
                    <Td fontSize="11px">{new Date(t.transaction_date).toLocaleDateString('en-GB')}</Td>
                    <Td fontSize="11px" fontWeight="700" color="blue.700">
                      £{parseFloat(t.amount).toFixed(2)}
                    </Td>
                    <Td fontSize="11px">{t.invoice_number || t.invoice_ref || '—'}</Td>
                    <Td fontSize="11px" color="gray.600">{t.description || '—'}</Td>
                    <Td fontSize="11px" color="gray.500">{t.reference || '—'}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" onClick={txDisclosure.onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    </>
  );
}
